import React from 'react';
import styles from './TimePicker.module.css';

interface TimePickerProps {
  value?: string | null; // 'HH:mm'
  onChange: (value: string | null) => void;
  disabled?: boolean;
  placeholder?: string;
  allowClear?: boolean;
}

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const MINUTES = Array.from({ length: 60 }, (_, i) => i);
const pad = (n: number) => String(n).padStart(2, '0');

const ClockIcon = () => (
  <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </svg>
);
const CloseX = () => (
  <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
    <path d="M6 6l12 12M18 6 6 18" />
  </svg>
);

const parse = (v?: string | null): { h: number | null; m: number | null } => {
  if (!v || !/^\d{1,2}:\d{2}/.test(v)) return { h: null, m: null };
  const [h, m] = v.slice(0, 5).split(':').map(Number);
  return { h, m };
};

const TimePicker: React.FC<TimePickerProps> = ({ value, onChange, disabled, placeholder = '--:--', allowClear = true }) => {
  const [open, setOpen] = React.useState(false);
  const wrapRef = React.useRef<HTMLDivElement>(null);
  const hourColRef = React.useRef<HTMLDivElement>(null);
  const minColRef = React.useRef<HTMLDivElement>(null);

  const { h, m } = parse(value);

  React.useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    requestAnimationFrame(() => {
      hourColRef.current?.querySelector<HTMLElement>(`[data-h="${h ?? 0}"]`)?.scrollIntoView({ block: 'center' });
      minColRef.current?.querySelector<HTMLElement>(`[data-m="${m ?? 0}"]`)?.scrollIntoView({ block: 'center' });
    });
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const pickHour = (hh: number) => onChange(`${pad(hh)}:${pad(m ?? 0)}`);
  const pickMinute = (mm: number) => onChange(`${pad(h ?? 0)}:${pad(mm)}`);

  return (
    <div ref={wrapRef} className={[styles.wrap, disabled ? styles.disabled : '', open ? styles.open : ''].join(' ')}>
      <button type="button" className={styles.trigger} disabled={disabled} onClick={() => !disabled && setOpen((o) => !o)}>
        <span className={value ? styles.value : styles.placeholder}>{value ? value.slice(0, 5) : placeholder}</span>
        <span className={styles.icon}><ClockIcon /></span>
      </button>
      {allowClear && value && !disabled && (
        <button type="button" className={styles.clear} onClick={() => onChange(null)} aria-label="Clear">
          <CloseX />
        </button>
      )}

      {open && (
        <div className={styles.panel}>
          <div ref={hourColRef} className={styles.col}>
            {HOURS.map((hh) => (
              <button
                key={hh}
                type="button"
                data-h={hh}
                className={[styles.item, h === hh ? styles.itemActive : ''].join(' ')}
                onClick={() => pickHour(hh)}
              >
                {pad(hh)}
              </button>
            ))}
          </div>
          <div ref={minColRef} className={styles.col}>
            {MINUTES.map((mm) => (
              <button
                key={mm}
                type="button"
                data-m={mm}
                className={[styles.item, m === mm ? styles.itemActive : ''].join(' ')}
                onClick={() => pickMinute(mm)}
              >
                {pad(mm)}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TimePicker;
